/**
 * Danger Zone — permanent account deletion.
 *
 * Lives at the bottom of Settings, visually separated from normal settings
 * via red accents. Click flow:
 *
 *   1. User clicks "Delete my account"
 *   2. Modal opens explaining what gets deleted
 *   3. User must type DELETE verbatim to enable the confirm button
 *   4. On confirm, calls DELETE /api/me, then signs out and redirects home
 *
 * Irreversible — no soft-delete grace period at this stage. The friction
 * of typing DELETE and reading the modal is the only safeguard.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { useAuth } from '../context/AuthContext.jsx';

export default function DangerZone() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="card danger-card">
        <span className="eyebrow danger-text">Danger zone</span>
        <h4 className="mt-8 mb-8">Delete your account</h4>
        <p className="muted mt-0">
          Permanently removes your profile, all items, all reports, every photo
          you've uploaded, and your sign-in. This cannot be undone — there is no
          grace period and no recovery.
        </p>
        <button
          type="button"
          className="btn btn-danger mt-8"
          onClick={() => setOpen(true)}
        >
          Delete my account
        </button>
      </div>

      {open && <DeleteAccountModal onClose={() => setOpen(false)} />}
    </>
  );
}

function DeleteAccountModal({ onClose }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const canConfirm = typed === 'DELETE' && !busy;

  // Escape closes the dialog (unless a delete is in flight).
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  async function confirm() {
    setBusy(true);
    setErr(null);
    try {
      await api.deleteAccount();
      signOut();
      navigate('/', { replace: true });
    } catch (e) {
      setErr(e?.message ?? 'Could not delete account');
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      onClick={onClose}
      className="dialog-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card dialog"
      >
        <h3 id="delete-modal-title" className="mt-0 italic">
          Delete your account permanently?
        </h3>
        <p className="muted">
          This will erase:
        </p>
        <ul className="muted dialog-list">
          <li>Your profile and notification settings</li>
          <li>Every item in your inventory and its photos</li>
          <li>Every found-report submitted via your code</li>
          <li>Your personal QR code and short URL</li>
          <li>Your sign-in account</li>
        </ul>
        <p className="muted mb-16">
          There is no recovery. Type <strong className="danger-text">DELETE</strong> below
          to confirm.
        </p>

        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="DELETE"
          autoFocus
          disabled={busy}
          aria-label='Type DELETE to confirm'
          className={`confirm-input${typed === 'DELETE' ? ' is-match' : ''}`}
        />

        {err && (
          <div className="alert error mt-12">
            {err}
          </div>
        )}

        <div className="row mt-16 justify-end">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={confirm}
            disabled={!canConfirm}
          >
            {busy ? 'Deleting…' : 'Delete forever'}
          </button>
        </div>
      </div>
    </div>
  );
}